import {
  IconAdjustmentsHorizontal,
  IconCode,
  IconCreditCard,
  IconFileText,
  IconFolder,
  IconHeadset,
  IconInbox,
  IconLayoutDashboard,
  IconListCheck,
  IconMail,
  IconMessageChatbot,
  IconMessageCircle,
  IconPackage,
  IconPlugConnected,
  IconReportAnalytics,
  IconRocket,
  IconSend,
  IconSettings2,
  IconTicket,
  IconUsers,
  IconUsersGroup,
  TablerIcon,
} from "@tabler/icons-react";
import classNames from "classnames";
import { ComponentType, LazyExoticComponent, ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Team } from "@common/features/Team";
import {
  DataResponse,
  Permissions,
  useFetchMeData,
  useIsTeammate,
} from "@common/features/User";
import { __ } from "@common/helpers/i18n";
import Logo from "@app/assets/Logo";
import { SubMenuItemsProps } from "@app/elements/container/SubMenuItems";
import { CounterBadge } from "@app/elements/text";
import { checkIfActive, withDashboardRootAlias } from "@app/helpers/menu";
import { useDocumentDirection, useLocalStorage } from "@app/hooks";

export type MenuProps = {
  generateMenu: (data: DataResponse) => MenuItemsType[];
  showChildMenus?: boolean;
};

export type ChildMenu = {
  url: string;
  label: string;
  icon?: ReactNode;
  teamPermission?: keyof Team["permissions"] | false;
  permission?: keyof Permissions | false;
  external?: boolean;
  unread?: () => ReactNode;
  exact?: boolean;
};

export type MenuItemsType = {
  url?: string;
  icon?: ReactNode;
  childs?: ChildMenu[];
  teamPermission?: keyof Team["permissions"] | false;
  permission?: keyof Permissions | false;
  label?: string;
  unread?: () => ReactNode;
  showSelectOnSmall?: boolean;
  Component?:
    | ComponentType<SubMenuItemsProps>
    | LazyExoticComponent<ComponentType<SubMenuItemsProps>>;
};

const menuIconEntries: Array<[string, TablerIcon]> = [
  ["Dashboard", IconLayoutDashboard],
  ["Publish", IconSend],
  ["Contents", IconFolder],
  ["Content", IconFileText],
  ["Comments", IconMessageCircle],
  ["Messages", IconInbox],
  ["Automation", IconMessageChatbot],
  ["Connect accounts", IconPlugConnected],
  ["Connected accounts", IconPlugConnected],
  ["Team Members", IconUsersGroup],
  ["Team members", IconUsersGroup],
  ["Support ticket", IconHeadset],
  ["Support tickets", IconHeadset],
  ["Settings", IconSettings2],
  ["Support", IconHeadset],
  ["Manage", IconUsers],
  ["Report", IconReportAnalytics],
  ["Posts", IconFileText],
  ["Files", IconFolder],
  ["Users", IconUsers],
  ["Orders", IconCreditCard],
  ["Applications", IconCode],
  ["Newsletters", IconMail],
  ["Vouchers", IconPackage],
  ["Ticket", IconTicket],
  ["Task", IconListCheck],
  ["Site settings", IconAdjustmentsHorizontal],
];

const menuIcons = Object.fromEntries(menuIconEntries) as Record<
  string,
  TablerIcon
>;

function getMenuIcon(label?: string) {
  if (!label) return undefined;

  return (
    menuIcons[label] ||
    menuIconEntries.find(([key]) => label === __(key as any))?.[1]
  );
}

export default function Menu({ generateMenu, showChildMenus }: MenuProps) {
  const location = useLocation();
  const { data } = useFetchMeData();
  const isTeamamte = useIsTeammate();
  const direction = useDocumentDirection();
  const [selectedAutomationAccount] = useLocalStorage<number | null>(
    "automation-account",
    null,
  );
  const menu = generateMenu ? generateMenu(data!) : [];

  const visibleMenu = menu.filter((item) => {
    if (
      item.permission !== undefined &&
      (item.permission === false || !data?.masterPermissions?.[item.permission])
    ) {
      return false;
    }

    if (
      isTeamamte &&
      item.teamPermission !== undefined &&
      (item.teamPermission === false ||
        !data!.masterUser!.permissions?.[item.teamPermission])
    ) {
      return false;
    }
    return true;
  });
  const isAppMenu = visibleMenu.some(
    (item) => item.url === "/dashboard" || item.url === "/post",
  );

  const settingsMenu = visibleMenu.find(
    (item) => item.label === __("Settings"),
  );
  const settingChild = (url: string, label: string, icon?: ReactNode) => {
    const child = settingsMenu?.childs?.find((item) => item.url === url);
    if (!child) return undefined;
    return {
      ...child,
      label,
      icon,
    };
  };

  const menuItem = (url: string, label: string, icon?: ReactNode) => {
    const item = visibleMenu.find((entry) => entry.url === url);
    return item
      ? {
          ...item,
          label,
          icon,
        }
      : undefined;
  };

  const parentItem = (
    originalLabel: string,
    label: string,
    icon: ReactNode | undefined,
    fallbackUrl: string,
  ) => {
    const item = visibleMenu.find((entry) => entry.label === originalLabel);
    return item
      ? {
          ...item,
          label,
          icon,
        }
      : {
          url: fallbackUrl,
          label,
          icon,
        };
  };

  const appMainMenu = [
    menuItem("/dashboard", __("Dashboard"), undefined),
    menuItem("/post", __("Publish"), undefined),
    parentItem(__("Contents"), __("Contents"), undefined, "/posts"),
    menuItem("/comment", __("Comments"), undefined),
    menuItem("/inbox", __("Messages"), undefined),
    parentItem(__("Automation"), __("Automation"), undefined, "/automation"),
  ].filter(Boolean) as MenuItemsType[];

  const appOtherMenu = [
    settingChild("/accounts", __("Connected accounts"), undefined),
    settingChild("/team", __("Team members"), undefined),
    settingChild("/ticket", __("Support tickets"), undefined),
    settingChild("/user/profile", __("Settings"), undefined),
  ].filter(Boolean) as MenuItemsType[];

  const mainMenu = isAppMenu ? appMainMenu : visibleMenu;
  const otherMenu = isAppMenu ? appOtherMenu : [];
  const showFreePlanUpsell = isAppMenu && data?.masterUser?.package_id === -1;

  const renderItem = (item: MenuItemsType, index: number) => {
    const url =
      item.url === "/automation" && selectedAutomationAccount
        ? `/automation/${selectedAutomationAccount}/response`
        : !item.url
          ? item.childs?.[0].url
          : item.url;
    const unread = item.unread?.();
    const Icon = getMenuIcon(item.label);
    const isActive = checkIfActive(
      location,
      withDashboardRootAlias([
        item.url,
        ...(item.childs || []).map((i) => i.url),
      ]),
    );
    const showChildren =
      showChildMenus && isActive && item.childs && item.childs.length > 0;

    return (
      <div key={index} className={classNames(showChildren && "space-y-1")}>
        <Link
          to={url || ""}
          aria-current={isActive ? "page" : undefined}
          className={classNames(
            isActive
              ? "border-border bg-card text-foreground shadow-soft ring-1 ring-border/60"
              : "border-transparent text-muted-foreground hover:border-border hover:bg-card hover:text-foreground",
            "group relative flex min-h-10 items-center rounded-md border px-3 py-2 text-sm font-semibold leading-5 transition-all duration-150 ease-in-out",
          )}
        >
          {Icon && (
            <span
              className={classNames(
                isActive
                  ? "text-primary"
                  : "text-muted-foreground group-hover:text-primary",
                "me-2.5 flex h-5 w-5 flex-shrink-0 items-center justify-center",
              )}
              aria-hidden="true"
            >
              <Icon size={18} stroke={2} />
            </span>
          )}
          {!Icon && (
            <span
              className={classNames(
                isActive
                  ? "text-primary"
                  : "text-muted-foreground group-hover:text-primary",
                "me-2.5 flex h-5 w-5 flex-shrink-0 items-center justify-center",
              )}
            />
          )}
          <span className="truncate">{item.label}</span>
          {unread && (
            <CounterBadge
              value={unread === true ? __("New") : unread}
              max={99}
              tone="danger"
              size="xs"
              className="ms-auto bg-destructive text-destructive-foreground opacity-100 ring-2 ring-card hover:bg-destructive"
            />
          )}
        </Link>

        {showChildren && (
          <div className="space-y-1 border-s border-border/80 ps-2">
            {item.childs!.map((child, childIndex) => {
              const isChildActive = checkIfActive(
                location,
                [child.url],
                child.exact,
              );
              const childUnread = child.unread?.();

              return (
                <Link
                  key={`${child.url}-${childIndex}`}
                  to={child.url}
                  target={child.external ? "_blank" : undefined}
                  aria-current={isChildActive ? "page" : undefined}
                  className={classNames(
                    isChildActive
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-card hover:text-foreground",
                    "group flex min-h-9 items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-semibold leading-5 transition-colors",
                  )}
                >
                  <span
                    className={classNames(
                      isChildActive
                        ? "bg-primary/10 text-primary"
                        : "bg-background text-muted-foreground group-hover:text-primary",
                      "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md [&_svg]:h-4 [&_svg]:w-4",
                    )}
                    aria-hidden="true"
                  >
                    {child.icon}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{child.label}</span>
                  {childUnread && (
                    <CounterBadge
                      value={childUnread === true ? __("New") : childUnread}
                      max={99}
                      tone="danger"
                      size="xs"
                      className="ms-auto bg-destructive text-destructive-foreground opacity-100 ring-2 ring-card hover:bg-destructive"
                    />
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <nav
      aria-label="Sidebar"
      className="hidden w-[270px] flex-shrink-0 flex-col border-border bg-muted/35 ltr:border-r rtl:border-l md:flex"
      dir={direction}
    >
      <a
        className="flex h-16 items-center justify-start border-b border-border px-3.5"
        href="/"
        aria-label={__(import.meta.env.VITE_SITE_NAME)}
      >
        <span className="flex min-w-0 items-center px-3">
          <Logo
            variant="full"
            className="h-7 w-auto max-w-[145px] flex-shrink-0"
          />
        </span>
      </a>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3.5 pb-4 pt-3">
        <div className="pb-2.5">
          <div className="space-y-1">{mainMenu.map(renderItem)}</div>
        </div>

        {otherMenu.length > 0 && (
          <div className="mb-3 border-t border-border pt-2.5">
            <div className="space-y-1">{otherMenu.map(renderItem)}</div>
          </div>
        )}

        {showFreePlanUpsell && (
          <div className="mt-auto rounded-lg border border-primary/15 bg-card p-3.5 shadow-soft">
            <div className="flex items-center gap-2 text-sm font-extrabold leading-5 text-foreground">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                <IconRocket size={17} stroke={2} />
              </span>
              {__("Upgrade subscription")}
            </div>
            <p className="mt-2 text-sm leading-5 text-muted-foreground">
              {__(
                "Choose a plan to increase account, publishing, and smart reply capacity.",
              )}
            </p>
            <Link
              to="/renew"
              className="mt-4 inline-flex h-10 w-full items-center justify-center gap-2 rounded-md border border-border bg-card text-sm font-bold text-primary transition-all duration-150 hover:bg-muted"
            >
              <IconRocket size={17} stroke={2} />
              {__("View plans")}
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
}
